/**
 * The legend doubles as the category filter (features.md §5). Visibility is
 * session state — it never touches the document, so toggling a filter does not
 * mark the file as modified.
 */

import type { Category, CategoryId } from '../model/types';
import { h, icon } from './editor/dom';

const SVG_NS = 'http://www.w3.org/2000/svg';

export interface FilterHandlers {
  onToggleCategory(id: CategoryId): void;
  onSetAllCategories(visible: boolean): void;
  onToggleBand(index: number): void;
  onSetAllBands(visible: boolean): void;
}

export interface FilterState {
  categories: ReadonlySet<CategoryId>;
  bands: ReadonlySet<number>;
}

/**
 * Ink that stays legible on a given fill: the category colours are pale, so the
 * tick is a much darker shade of the colour itself rather than a foreign black —
 * except on a dark category, where it lightens instead.
 */
function contrastInk(hex: string): string {
  const value = hex.replace('#', '');
  const full = value.length === 3 ? [...value].map((c) => c + c).join('') : value;
  const packed = Number.parseInt(full, 16);
  if (!Number.isFinite(packed)) return '#1a1a1a';

  const [r, g, b] = [(packed >> 16) & 255, (packed >> 8) & 255, packed & 255];
  const luminance = (0.299 * (r ?? 0) + 0.587 * (g ?? 0) + 0.114 * (b ?? 0)) / 255;

  const mix = (channel: number) =>
    Math.round(luminance < 0.45 ? channel + (255 - channel) * 0.85 : channel * 0.4);

  return `rgb(${mix(r ?? 0)}, ${mix(g ?? 0)}, ${mix(b ?? 0)})`;
}

/**
 * The tick drawn inside a visible category's chip.
 *
 * The SVG fills the chip edge to edge, so centring is decided entirely by where
 * the path sits in the viewBox — no flex centring, no baseline, no rounding
 * between two different pixel sizes. A checkmark's ink sits high and left of its
 * own bounding box, so the path is placed by its extents rather than eyeballed:
 * x 4→12 and y 5→11 both centre on 8, and the 2.2 stroke leaves a clear margin
 * inside the 16-unit box.
 */
function checkmark(color: string): SVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 16 16');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', contrastInk(color));
  svg.setAttribute('stroke-width', '2.2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');

  const path = document.createElementNS(SVG_NS, 'path');
  path.setAttribute('d', 'M4 8.1L6.9 11L12 5');
  svg.append(path);

  return svg;
}

/** A toggle whose chip carries the tick: one row of these per filter group. */
function toggle(options: {
  label: string;
  color: string;
  visible: boolean;
  hint: string;
  dataset: Record<string, string>;
  onClick: () => void;
}): HTMLButtonElement {
  const swatch = h('button', {
    class: options.visible ? 'swatch' : 'swatch off',
    title: options.hint,
    dataset: options.dataset,
  });
  swatch.setAttribute('aria-pressed', String(options.visible));
  swatch.addEventListener('click', options.onClick);

  const chip = h('i', {});
  chip.style.background = options.color;
  if (options.visible) chip.append(checkmark(options.color));

  swatch.append(chip, document.createTextNode(options.label));
  return swatch;
}

function group(
  title: string,
  count: number,
  hiddenCount: number,
  onSetAll: (visible: boolean) => void,
  actionPrefix: string,
  swatches: HTMLElement[],
): HTMLElement {
  const showAll = h('button', {
    text: 'Tout cocher',
    disabled: hiddenCount === 0,
    dataset: { action: `${actionPrefix}-all` },
  });
  showAll.addEventListener('click', () => onSetAll(true));

  const hideAll = h('button', {
    text: 'Tout décocher',
    disabled: hiddenCount === count,
    dataset: { action: `${actionPrefix}-none` },
  });
  hideAll.addEventListener('click', () => onSetAll(false));

  return h(
    'div',
    { class: 'filter-group' },
    h('span', { class: 'filter-label', text: title }),
    showAll,
    hideAll,
    h('div', { class: 'filter-swatches' }, ...swatches),
  );
}

export function renderFilterBar(
  container: HTMLElement,
  doc: { categories: Record<CategoryId, Category>; bands: ReadonlyArray<{ label: string }> },
  hidden: FilterState,
  handlers: FilterHandlers,
): void {
  container.replaceChildren();

  const heading = h('span', { class: 'filter-title' });
  heading.append(icon('filter'), document.createTextNode('Filtres'));
  container.append(heading);

  const categorySwatches = Object.entries(doc.categories).map(([id, category]) =>
    toggle({
      label: category.name,
      color: category.color,
      visible: !hidden.categories.has(id),
      hint: hidden.categories.has(id) ? 'Afficher cette catégorie' : 'Masquer cette catégorie',
      dataset: { category: id },
      onClick: () => handlers.onToggleCategory(id),
    }),
  );

  const categoryIds = Object.keys(doc.categories);
  container.append(
    group(
      'Catégories',
      categoryIds.length,
      categoryIds.filter((id) => hidden.categories.has(id)).length,
      handlers.onSetAllCategories,
      'filter',
      categorySwatches,
    ),
  );

  // Bands have no colour of their own; the chip is a plain checkbox.
  const bandSwatches = doc.bands.map((band, index) =>
    toggle({
      label: band.label,
      color: '#ffffff',
      visible: !hidden.bands.has(index),
      hint: hidden.bands.has(index) ? 'Afficher cette période' : 'Masquer cette période',
      dataset: { band: String(index) },
      onClick: () => handlers.onToggleBand(index),
    }),
  );

  container.append(
    group(
      'Périodes',
      doc.bands.length,
      doc.bands.filter((_, index) => hidden.bands.has(index)).length,
      handlers.onSetAllBands,
      'filter-band',
      bandSwatches,
    ),
  );
}
